/**
 * PostHog — analytics de produs (funnel-uri, session replay) pe langa App Insights.
 *
 * App Insights ramane pentru diagnostic (erori, corelare browser -> backend). PostHog raspunde la
 * „ce fac oamenii": unde abandoneaza inscrierea, cum folosesc aplicatia dupa.
 *
 * Poarta de activare: IS_PRODUCTION && VITE_POSTHOG_KEY. Fara cheie, toate exporturile sunt no-op
 * (dev local, build fara variabila) - apelantii nu verifica nimic.
 *
 * Date personale (utilizatori din Romania, printre ei minori):
 * - `identify` primeste DOAR id-ul intern + rolul/editia; niciodata email, nume sau telefon;
 * - session replay cu TOATE input-urile mascate (parole, coduri OTP, telefoane, formulare);
 * - tokenul de colaborator e redactat din URL-uri in `before_send` (vezi `posthog-sanitize.ts`);
 * - host EU implicit (`VITE_POSTHOG_HOST`), ca datele sa ramana in UE.
 */

import posthog from "posthog-js";

import { IS_PRODUCTION } from "@/constants/config";
import { detecteazaBrowserInApp } from "@/shared/utils/in-app-browser";

import { sanitizePostHogEvent } from "./posthog-sanitize";

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com";

let enabled = false;
let identifiedId: string | undefined;

if (IS_PRODUCTION && key) {
    posthog.init(key, {
        api_host: host,
        defaults: "2026-08-30",
        // Pageview pe fiecare schimbare de ruta din SPA (react-router foloseste history API).
        capture_pageview: "history_change",
        capture_pageleave: true,
        // Profil de persoana doar dupa login; vizitatorii anonimi raman evenimente ieftine, dar se
        // leaga retroactiv de profesor la `identify` (pasii OTP de dinaintea contului).
        person_profiles: "identified_only",
        capture_exceptions: true,
        session_recording: {
            maskAllInputs: true,
        },
        before_send: (event) => sanitizePostHogEvent(event),
    });
    // Aceeasi eticheta ca pe App Insights (`inAppBrowser`), pe FIECARE eveniment: funnel-ul de
    // inscriere se poate taia dupa container (WebView-ul Facebook vs. browser obisnuit).
    posthog.register({ inAppBrowser: detecteazaBrowserInApp(navigator.userAgent) });
    enabled = true;
}

export function identifyPostHogUser(
    userId: string,
    props: { role?: string; editionId?: string; schoolId?: string },
): void {
    if (!enabled || identifiedId === userId) return;
    identifiedId = userId;
    posthog.identify(userId, {
        role: props.role,
        editionId: props.editionId,
        schoolId: props.schoolId,
    });
}

export function resetPostHogUser(): void {
    if (!enabled || identifiedId === undefined) return;
    identifiedId = undefined;
    // Doar la logout real: un reset la momentul gresit rupe sesiunea in doua.
    posthog.reset();
}

/** Legatura spre App Insights: acelasi id pe telemetria Azure duce la sesiunea din PostHog. */
export function getPostHogSessionId(): string | undefined {
    return enabled ? posthog.get_session_id() || undefined : undefined;
}

/** URL-ul replay-ului la momentul curent. Replay-ul e esantionat, deci poate sa nu existe. */
export function getPostHogReplayUrl(): string | undefined {
    return enabled ? posthog.get_session_replay_url({ withTimestamp: true, timestampLookBack: 30 }) : undefined;
}

export function capturePostHog(event: string, properties?: Record<string, unknown>): void {
    if (!enabled) return;
    posthog.capture(event, properties);
}
